'use client';

import { Check, ShieldCheck, Zap, Lock, ArrowRight } from 'lucide-react';
import Button from '../ui/Button';
import FadeIn from '../ui/FadeIn';
import { useTranslations } from '@/lib/i18n/TranslationProvider';

export default function Pricing() {
    const { t } = useTranslations();

    // Show: Yearly (index 3), Monthly (index 2), Day Pass (index 1) — in that order
    const activePlans = [t.pricing.plans[3], t.pricing.plans[2], t.pricing.plans[1]];

    const originalPrices = [null, null, null];
    const highlights = [true, false, false];
    const styles: ('subtle' | 'standard' | 'highlight')[] = ['highlight', 'standard', 'standard'];
    const hrefs = Array(activePlans.length).fill("https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi");

    const plans = activePlans.map((plan, i) => ({
        ...plan,
        originalPrice: originalPrices[i],
        highlight: highlights[i],
        badge: i === 0 ? "Most Popular" : null,
        style: styles[i],
        buttonHref: hrefs[i],
    }));

    return (
        <section id="pricing" className="section-padding relative overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 relative z-10 w-full max-w-[1400px]">

                {/* Header: Editorial Scale */}
                <div className="text-center mb-24 relative z-10">
                    <FadeIn direction="up" yOffset={40}>
                        <h2 className="text-[clamp(3.5rem,10vw,6.5rem)] font-black text-[var(--color-md-on-surface)] tracking-[-0.05em] leading-[0.9] mb-8 text-balance">
                            {t.pricing.heading} <span className="text-brand-gradient text-glow-primary">{t.pricing.headingAccent}</span>
                        </h2>
                        <p className="text-[var(--color-md-on-surface-variant)] text-xl md:text-2xl font-medium max-w-3xl mx-auto opacity-80 text-balance">
                            {t.pricing.subtitle}
                        </p>
                    </FadeIn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mx-auto mb-16 max-w-6xl xl:max-w-7xl">
                    {plans.map((plan, index) => (
                        <FadeIn key={index} delay={index * 0.1} direction="up" yOffset={40} className="h-full">
                            <div className={`h-full kinetic-card p-10 flex flex-col relative ${plan.style === 'highlight'
                                ? 'border-2 border-[var(--color-md-primary)] shadow-[0_0_40px_-10px_color-mix(in_srgb,var(--color-md-primary)_25%,transparent)] z-10 scale-[1.05]'
                                : 'opacity-90 hover:opacity-100'
                                }`}>

                                {/* Top Badges */}
                                {plan.badge && (
                                    <div className="absolute top-5 right-5 lg:top-6 lg:right-6 z-20">
                                        <span className={`text-[10px] uppercase font-bold tracking-[0.1em] px-3 py-1.5 rounded-full shadow-sm ${plan.style === 'highlight' ? 'bg-[var(--color-md-primary)] text-[var(--color-md-on-primary)] shadow-[var(--shadow-focused)]' : 'bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface-variant)] border border-[var(--color-md-outline-variant)]/50'
                                            }`}>
                                            {plan.badge}
                                        </span>
                                    </div>
                                )}

                                <div className="flex flex-col flex-grow">
                                    <div className="flex flex-col min-h-[220px] shrink-0">
                                        <h3 className="text-2xl font-black mb-3 pr-20 uppercase tracking-[0.05em] text-[var(--color-md-on-surface)]">
                                            {plan.name}
                                        </h3>

                                        <p className="text-base font-medium mb-8 min-h-[48px] leading-relaxed text-[var(--color-md-on-surface-variant)] opacity-70">
                                            {plan.description}
                                        </p>

                                        <div className="mt-auto mb-10">
                                            <div className="flex items-baseline gap-2">
                                                <div className="relative">
                                                    {plan.originalPrice && (
                                                        <span className="text-sm line-through text-[var(--color-md-primary)]/40 font-black absolute -top-6 left-0">{plan.originalPrice}</span>
                                                    )}
                                                    <span className={`text-6xl font-black tracking-[-0.05em] leading-none ${plan.style === 'highlight' ? 'text-[var(--color-md-primary)] text-glow-primary' : 'text-[var(--color-md-on-surface)]'}`}>
                                                        {plan.price}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-black uppercase tracking-widest text-[var(--color-md-on-surface-variant)]">
                                                    {plan.period}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Features List */}
                                    <ul className="space-y-4 flex-1 mb-8">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <div className={`w-5 h-5 shrink-0 mt-0.5 ${
                                                    plan.style === 'highlight' 
                                                        ? 'm3-icon-tonal !w-5 !h-5 scale-90' 
                                                        : 'm3-icon-secondary !w-5 !h-5 scale-90'
                                                }`}>
                                                    <Check className="w-3 h-3 stroke-[4px]" />
                                                </div>
                                                <span className={`text-[length:var(--font-body-medium)] leading-tight ${feature.includes('Everything in') ? 'text-[var(--color-md-on-surface-variant)] italic font-medium' : 'text-[var(--color-md-on-surface)] font-medium'} ${plan.highlight && feature.includes('40%') ? 'text-[var(--color-md-primary)] font-bold !not-italic' : ''}`}>
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* CTA Button: Physical Depth */}
                                <div className="mt-auto pt-8 border-t border-[var(--color-md-outline)]/10">
                                    <Button
                                        href={plan.buttonHref}
                                        className={`w-full h-14 text-base flex items-center justify-center font-black uppercase tracking-widest rounded-xl transition-all duration-300 ${plan.style === 'highlight'
                                            ? 'btn-glow shadow-cinematic-2 hover:shadow-cinematic-3'
                                            : 'bg-[var(--color-md-surface-container-high)] text-[var(--color-md-on-surface)] border border-[var(--color-md-outline)] hover:bg-[var(--color-md-outline)]/10'
                                            }`}
                                    >
                                        <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                                            <span>{plan.buttonText}</span>
                                            <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${plan.style === 'highlight' ? 'opacity-100' : 'opacity-40'}`} />
                                        </span>
                                    </Button>
                                    {plan.period !== 'Forever' && plan.style !== 'subtle' && (
                                        <p className="text-center text-[10px] mt-6 font-black uppercase tracking-[0.1em] text-[var(--color-md-outline)] opacity-70">
                                            {plan.period.includes('month') || plan.period.includes('year') ? t.pricing.billedAuto : t.pricing.oneTime}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>

            {/* Full-Width Identity Insurance / Vault Banner */}
            <div className="relative my-32 lg:my-48 overflow-hidden border-y border-[var(--color-md-primary)]/20 bg-[var(--color-md-surface-container-low)]">
                {/* Background Textures/Gradients */}
                <div className="absolute inset-0 opacity-10" 
                     style={{ 
                         backgroundImage: `radial-gradient(circle at 0% 0%, var(--color-md-primary) 0%, transparent 40%), 
                                           radial-gradient(circle at 100% 100%, var(--color-md-primary) 0%, transparent 40%)` 
                     }}>
                </div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03]"></div>
                
                <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-[1200px]">
                    <FadeIn direction="up" yOffset={40} className="py-12 md:py-16 lg:py-20">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                            
                            {/* Visual Side: The "Vault" Iconography */}
                            <div className="lg:col-span-4 flex items-center justify-center">
                                <div className="relative">
                                    <div className="absolute -inset-8 bg-[var(--color-md-primary)] opacity-10 blur-[40px] rounded-full"></div>
                                    <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-[2.5rem] bg-gradient-to-br from-[var(--color-md-primary)] to-[color-mix(in_srgb,var(--color-md-primary)_70%,black)] flex items-center justify-center shadow-[0_0_40px_-5px_var(--color-md-primary)] border border-white/10 group">
                                        <ShieldCheck className="w-16 h-16 md:w-20 md:h-20 text-[var(--color-md-on-primary)] transform group-hover:scale-105 transition-transform duration-500" />
                                        
                                        {/* Scanning Line Effect */}
                                        <div className="absolute inset-0 overflow-hidden rounded-[2.5rem]">
                                            <div className="w-full h-[1.5px] bg-white/30 absolute top-0 left-0 animate-[scan_4s_linear_infinite] shadow-[0_0_10px_white]"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content Side */}
                            <div className="lg:col-span-8 flex flex-col items-center lg:items-start text-center lg:text-left">
                                <div className="flex items-center gap-3 bg-[var(--color-md-primary)]/10 border border-[var(--color-md-primary)]/20 px-3 py-1 rounded-full mb-6">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)] animate-pulse"></div>
                                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[var(--color-md-primary)] pt-0.5">
                                        {t.pricing.vaultBanner.badge}
                                    </span>
                                </div>
                                
                                <h2 className="text-3xl md:text-5xl font-black text-[var(--color-md-on-surface)] mb-6 tracking-[-0.04em] leading-[1.1] text-balance">
                                    {t.pricing.vaultBanner.title}
                                </h2>
                                
                                <p className="text-lg md:text-xl text-[var(--color-md-on-surface-variant)] opacity-90 leading-relaxed mb-8 max-w-2xl text-balance">
                                    {t.pricing.vaultBanner.description}
                                </p>

                                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-5 pt-7 border-t border-[var(--color-md-outline)]/10 w-full">
                                    <div className="flex items-center gap-2.5 px-4 py-2 bg-[var(--color-md-primary)]/5 rounded-xl border border-[var(--color-md-primary)]/10">
                                        <Check className="w-4 h-4 text-[var(--color-md-primary)] stroke-[3px]" />
                                        <span className="text-[11px] font-black uppercase tracking-wider text-[var(--color-md-on-surface)]">
                                            {t.pricing.vaultBanner.exclusivity}
                                        </span>
                                    </div>
                                    
                                    <p className="text-xs font-bold text-[var(--color-md-primary)]/50 italic">
                                        {t.pricing.vaultBanner.subtitle}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                </div>

                {/* Background Visual Noise */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                    <div className="absolute -top-1/2 -left-1/4 w-[150%] h-[150%] bg-gradient-to-br from-transparent via-[var(--color-md-primary)]/5 to-transparent rotate-12 opacity-50"></div>
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10 w-full max-w-[1400px]">

                {/* Ideal Plan Text */}
                <FadeIn direction="up" yOffset={10} delay={0.3}>
                    <div className="flex justify-center mt-12 mb-24 px-4 text-center">
                        <p className="text-[var(--color-md-on-surface-variant)] text-[length:var(--font-body-small)] tracking-[var(--tracking-body-small)] font-medium">
                            {t.pricing.noneOfThese} <a href="mailto:support@wavault.com" className="text-[var(--color-md-on-surface)] hover:text-[var(--color-md-primary)] transition-colors underline decoration-[var(--color-md-outline-variant)] underline-offset-4">{t.pricing.tellUs}</a>
                        </p>
                    </div>
                </FadeIn>

                {/* Trust Strip Footer */}
                <FadeIn direction="up" yOffset={20} delay={0.4}>
                    <div className="flex flex-col items-center pt-8 border-t border-[var(--color-md-outline-variant)]/20 max-w-3xl mx-auto">
                        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-12 text-[var(--color-md-on-surface-variant)] text-[10px] md:text-xs font-bold tracking-[0.1em] uppercase">
                            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-[var(--color-md-primary)]" /> {t.pricing.secureCheckout}</div>
                            <div className="flex items-center gap-2"><Zap className="w-4 h-4 md:w-5 md:h-5 text-[var(--color-md-primary)]" /> {t.pricing.instantActivation}</div>
                            <div className="flex items-center gap-2"><Lock className="w-4 h-4 md:w-5 md:h-5 text-[var(--color-md-primary)]" /> {t.pricing.privateData}</div>
                        </div>
                    </div>
                </FadeIn>

            </div>
        </section>
    );
}
